/* eslint-disable complexity */
import React, { useMemo, useCallback, useRef } from 'react';
import { ProgressBarChart } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChart';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { TimeSeriesTrendsRequest } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { TimeSeriesTrendsResult } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { createGroupLabelsMapForComparisonMode } from 'libs/end-to-end/src/progress';
import { createProgressLineChartConfig } from 'libs/end-to-end/src/progress';
import { EVENT_STUDY_DATE_FORMATTER } from 'libs/end-to-end/src/progress';
import { LevelValue } from 'libs/end-to-end/src/progress';
import { transformProgressDataForPresentation } from 'libs/end-to-end/src/progress';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { NotEnoughData } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageErrorStates';
import { HasFailure } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageErrorStates';
import { TimeTrendsExplanation } from 'apps/sigma2-ui/src/pages/ProgressPage/TimeTrendsExplanation';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE } from 'libs/end-to-end/src/progress';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { transformEventStudyDataToMetricChanges } from 'libs/end-to-end/src/progress';
import { customVizRequestHasBreakdown } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { MetricChangeSection } from 'apps/sigma2-ui/src/pages/ProgressPage/MetricChangeSection';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getAllDimensionsFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { formatDateStandard } from 'libs/end-to-end/src/dates';
import { tryParseISO } from 'libs/end-to-end/src/dates';
import { isEventStudyEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { TimeSeriesLineChart } from 'apps/sigma2-ui/src/pages/ProgressPage/TimeSeriesLineChart';
import { ProgressPageChartTooltip } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageChartTooltip';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { OverallDisplayModeFilter } from 'apps/sigma2-ui/src/components/OverallDisplayModeFilter';
import cn from 'classnames';

const ProgressSpinner = ({ isLoading }: { isLoading: boolean }) => {
  return (
    isLoading && (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        w="full"
        flex="1"
      >
        <CustomVizCircularSpinner />
      </Box>
    )
  );
};

/**
 * Get treatment group and selected group by value from the custom viz request. Custom viz only stores the column value,
 * so we have to find from which dimension or secondary filter that value was selected.
 */
const getTreatmentGroup = ({
  customVizRequest,
  treatmentGroup,
  myObservation,
}: {
  treatmentGroup: string | null;
  myObservation: MyObservation;
  customVizRequest: TimeSeriesTrendsRequest;
}): [string, string] | null => {
  if (!treatmentGroup) {
    return null;
  }

  const allDimensions = getAllDimensionsFromObservation(myObservation);
  const dimension = allDimensions.find((d) =>
    d.levels.some((l) => l.columnValue === treatmentGroup),
  );

  if (dimension) {
    return [dimension.columnHeaderValue, treatmentGroup];
  }

  const filters = customVizRequest.inputs.extras.filters ?? [];
  const selectedFilter = filters.find(
    (f) => f.values.find((v: string) => v === treatmentGroup) !== undefined,
  );

  if (selectedFilter) {
    return [selectedFilter.columnHeaderValue, treatmentGroup];
  }

  return null;
};

export const getTimeSeriesTrendsPrimaryFilterSelections = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: TimeSeriesTrendsRequest;
  myObservation: MyObservation;
}) => {
  const extras = customVizRequest.inputs.extras;
  const metric = extras.metric;
  const breakdownOption = extras.breakdownOption;
  const viewBy = extras.timeBoiOption;
  const bigTimeComparisonOption = extras.bigTimeComparisonOption;
  const endDate = extras.endDate;
  const startDate = extras.startDate;
  const dateRangeType = extras.dateRangeType;
  const chartType = extras.chartType ?? 'bar';
  const treatmentDate = extras.treatmentDate;
  const treatmentGroup = extras.treatmentGroup;
  // add stuff

  const focusArea = myObservation.focusAreas.find((fa) =>
    fa.metrics.some((m) => m.columnHeaderValue === metric),
  )?.focusArea;

  if (!focusArea || !breakdownOption || !dateRangeType) {
    return;
  }

  const now = new Date();

  const primaryFilterSelections: PrimaryFilterSelection[] = [
    {
      primaryFilterKey: 'focusArea',
      value: [focusArea, metric],
    },
    {
      primaryFilterKey: 'groupBy',
      value: breakdownOption,
    },
    {
      primaryFilterKey: 'viewBy',
      value: viewBy,
    },
    {
      primaryFilterKey: 'bigTimePeriodComparison',
      value: bigTimeComparisonOption,
    },
    {
      primaryFilterKey: 'dateRange',
      value: {
        startDate: tryParseISO(startDate) ?? now,
        endDate: tryParseISO(endDate) ?? now,
        dateRangeType: dateRangeType,
      },
    },
    {
      primaryFilterKey: 'chartType',
      value: chartType,
    },
  ];

  const treatmentDateParsed = treatmentDate ? tryParseISO(treatmentDate) : null;

  if (treatmentDateParsed) {
    primaryFilterSelections.push({
      primaryFilterKey: 'singleDate',
      value: formatDateStandard({
        date: treatmentDateParsed,
        formatter: EVENT_STUDY_DATE_FORMATTER,
      }),
    });
  } else {
    primaryFilterSelections.push({
      primaryFilterKey: 'singleDate',
      value: EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE,
    });
  }

  const selectedTreatmentGroup =
    treatmentGroup &&
    getTreatmentGroup({
      customVizRequest,
      treatmentGroup,
      myObservation,
    });

  if (treatmentDateParsed && selectedTreatmentGroup) {
    primaryFilterSelections.push({
      primaryFilterKey: 'eventStudyGroup',
      value: selectedTreatmentGroup,
    });
  } else {
    primaryFilterSelections.push({
      primaryFilterKey: 'eventStudyGroup',
      value: [],
    });
  }

  return primaryFilterSelections;
};

export const ProgressBarChartWithDatesCard = ({
  customVizRequest,
  result,
  status,
  viewConfiguration,
  setViewConfiguration,
  noPadding,
}: {
  customVizRequest: TimeSeriesTrendsRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
  setViewConfiguration?: (viewConfigurations: ViewConfigurationSchema) => void;
  viewConfiguration: ViewConfigurationSchema;
  noPadding?: boolean;
}) => {
  const { myObservation } = useObservation();
  const { getMetricByName } = useMetricConfig();

  const isEventStudyEnabled = useMemo(
    () => isEventStudyEnabledForRevision({ revisionFlags: myObservation }),
    [myObservation],
  );

  const {
    data: graphData,
    isLoading,
    readyToDisplayDynamicContent,
    isNotEnoughDataState,
    hasFailed,
  } = useMemo(() => {
    return parseCustomVizResult({
      result,
      status,
      ResultZodType: TimeSeriesTrendsResult,
    });
  }, [result, status]);

  const metricDefinition = useMemo(() => {
    if (!myObservation || !customVizRequest) {
      return;
    }

    const primaryFilterSelections = getTimeSeriesTrendsPrimaryFilterSelections({
      customVizRequest,
      myObservation,
    });
    if (!primaryFilterSelections) {
      return;
    }

    const focusArea = getFocusArea(primaryFilterSelections);

    if (!focusArea) {
      return;
    }

    return getMetricByName({
      focusArea: focusArea.focusArea,
      metricName: focusArea.metric,
      myObservation,
    });
  }, [getMetricByName, customVizRequest, myObservation]);

  const focusArea = useMemo(() => {
    if (!myObservation || !customVizRequest) {
      return;
    }

    const primaryFilterSelections = getTimeSeriesTrendsPrimaryFilterSelections({
      customVizRequest,
      myObservation,
    });
    if (!primaryFilterSelections) {
      return;
    }

    return getFocusArea(primaryFilterSelections)?.focusArea;
  }, [customVizRequest, myObservation]);

  // We just don't want to show the overall switcher on event study charts for now.
  const vizRequestIsEventStudy = useMemo(() => {
    if (!customVizRequest) {
      return false;
    }

    return Boolean(
      customVizRequest.inputs.extras.treatmentDate &&
        customVizRequest.inputs.extras.treatmentDate !==
          EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE,
    );
  }, [customVizRequest]);

  const eventStudyMetricChanges = useMemo(() => {
    if (
      !readyToDisplayDynamicContent ||
      !customVizRequest?.inputs.extras.treatmentDate ||
      customVizRequest.inputs.extras.treatmentDate ===
        EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE
    ) {
      return null;
    }
    return transformEventStudyDataToMetricChanges(
      graphData,
      customVizRequest.inputs.extras.treatmentDate,
    );
  }, [readyToDisplayDynamicContent, customVizRequest, graphData]);

  const filteredData = useMemo(
    () =>
      transformProgressDataForPresentation({
        data: graphData,
        treatmentDate: customVizRequest?.inputs.extras.treatmentDate,
        treatmentGroup: customVizRequest?.inputs.extras.treatmentGroup,
      }),
    [
      graphData,
      customVizRequest?.inputs.extras.treatmentDate,
      customVizRequest?.inputs.extras.treatmentGroup,
    ],
  );

  // Get the current metric definition based on the custom viz request
  const currentMetricDefinition = useMemo(() => {
    if (!customVizRequest?.inputs?.extras?.metric || !myObservation) {
      return null;
    }

    const definition = myObservation.focusAreas
      .flatMap((fa) => fa.metrics)
      .find(
        (m) => m.columnHeaderValue === customVizRequest.inputs.extras.metric,
      );

    return definition ?? null;
  }, [customVizRequest, myObservation]);

  const currentMetricUnits = useMemo(() => {
    if (
      !currentMetricDefinition ||
      !customVizRequest?.inputs?.extras?.metric ||
      !myObservation
    ) {
      return null;
    }

    const focusArea = myObservation.focusAreas.find((fa) =>
      fa.metrics.some(
        (m) => m.columnHeaderValue === customVizRequest.inputs.extras.metric,
      ),
    )?.focusArea;

    if (!focusArea) {
      return null;
    }

    return determineUnitOfMetric({
      knownMaximum: null,
      isMetricBoolean: currentMetricDefinition.isMetricBoolean,
      metricFocusArea: focusArea,
      definitionUnit: currentMetricDefinition.unit,
    });
  }, [currentMetricDefinition, customVizRequest, myObservation]);

  const tooltipForValue = useCallback(
    (groupId: string, value: LevelValue) => {
      return (
        <ProgressPageChartTooltip
          groupId={groupId}
          value={value}
          currentMetricDefinition={currentMetricDefinition}
          filteredData={filteredData}
          metricUnits={currentMetricUnits}
          inlineMode={true}
        />
      );
    },
    [currentMetricDefinition, filteredData, currentMetricUnits],
  );

  const selectedChartTypeOption = useMemo(() => {
    return customVizRequest?.inputs.extras.chartType ?? 'bar';
  }, [customVizRequest]);

  const downloadContainerRef = useRef<HTMLDivElement>(null);

  const isBreakdown = useMemo(() => {
    if (!customVizRequest) {
      return false;
    }

    return customVizRequestHasBreakdown(customVizRequest);
  }, [customVizRequest]);

  const lineChartConfig = useMemo(
    () =>
      createProgressLineChartConfig({
        graphData,
        treatmentDate: customVizRequest?.inputs.extras.treatmentDate,
        treatmentGroup: customVizRequest?.inputs.extras.treatmentGroup,
        isBreakdown,
      }),
    [
      customVizRequest?.inputs.extras.treatmentDate,
      customVizRequest?.inputs.extras.treatmentGroup,
      graphData,
      isBreakdown,
    ],
  );

  const language = useLanguage();

  const isEventStudyNotEnoughData = useMemo(() => {
    if (
      !isEventStudyEnabled ||
      !customVizRequest?.inputs.extras.treatmentDate ||
      !readyToDisplayDynamicContent
    ) {
      return false;
    }

    return (
      graphData?.jump == null ||
      graphData?.beforeJump == null ||
      graphData?.afterJump == null ||
      graphData?.buckets.length === 0
    );
  }, [
    customVizRequest?.inputs.extras.treatmentDate,
    graphData?.afterJump,
    graphData?.beforeJump,
    graphData?.buckets.length,
    graphData?.jump,
    isEventStudyEnabled,
    readyToDisplayDynamicContent,
  ]);

  const isMetricBoolean = useMemo(() => {
    // Trends is a little strange and while there is a metricDefinition.isMetricBoolean,
    // the buckets elements also have isMetricBoolean flags. Use this instead.
    return (
      graphData?.buckets.some((bucket) =>
        bucket.elements.some((el) => el.isMetricBoolean === true),
      ) ?? false
    );
  }, [graphData?.buckets]);

  const groupLabels = useMemo(() => {
    return createGroupLabelsMapForComparisonMode({
      primaryBucketToComparisonBucketData:
        filteredData.primaryBucketToComparisonBucketData,
    });
  }, [filteredData.primaryBucketToComparisonBucketData]);

  return (
    <div
      className={cn('flex flex-col w-full h-full min-h-96', {
        'p-6': !noPadding,
      })}
    >
      {isEventStudyEnabled &&
        metricDefinition &&
        focusArea &&
        eventStudyMetricChanges &&
        readyToDisplayDynamicContent &&
        !isEventStudyNotEnoughData && (
          <div className="px-11 mt-4">
            <MetricChangeSection
              metricDefinition={metricDefinition}
              focusArea={focusArea}
              beforeJump={eventStudyMetricChanges.beforeJump}
              jump={eventStudyMetricChanges.jump}
              afterJump={eventStudyMetricChanges.afterJump}
            />
            <hr className="border-0 border-t border-[#E7E8E8] my-4" />
          </div>
        )}

      <ProgressSpinner isLoading={isLoading} />

      <Spacing size={'md'} direction={'vertical'}>
        {isNotEnoughDataState && <NotEnoughData />}

        {isEventStudyNotEnoughData && <NotEnoughData isEventStudy={true} />}

        {hasFailed && !isNotEnoughDataState && <HasFailure />}

        {readyToDisplayDynamicContent &&
          !isEventStudyNotEnoughData &&
          currentMetricDefinition &&
          currentMetricUnits && (
            <>
              {!vizRequestIsEventStudy && (
                <OverallDisplayModeFilter
                  viewConfiguration={viewConfiguration}
                  setViewConfiguration={setViewConfiguration}
                />
              )}
              {selectedChartTypeOption === 'bar' && (
                <div
                  ref={downloadContainerRef}
                  className="divide-y-[1px] divide-gray-200 divide-solid flex flex-col gap-2"
                >
                  <ProgressBarChart
                    data={filteredData.data}
                    metricDisplayName={currentMetricDefinition.displayName}
                    units={currentMetricUnits}
                    comparisonGroupValue=""
                    groupLabels={groupLabels}
                    getLinkToDeepDive={async () => ''}
                    overridingTooltip={tooltipForValue}
                    displayHorizontally={true}
                    whiteLabelingSubjectPlural={
                      currentMetricDefinition.whiteLabeling.subjectPlural
                    }
                    xAxisLabel={language.progressChartXAxisLabel()}
                    viewConfiguration={viewConfiguration}
                  />
                </div>
              )}
              {selectedChartTypeOption === 'line' && (
                <div ref={downloadContainerRef}>
                  <Spacing size="lg" direction="vertical">
                    <TimeSeriesLineChart
                      units={currentMetricUnits}
                      metricDefinition={currentMetricDefinition}
                      viewConfiguration={viewConfiguration}
                      data={graphData}
                      treatmentDate={
                        customVizRequest?.inputs.extras.treatmentDate
                      }
                      treatmentGroup={
                        customVizRequest?.inputs.extras.treatmentGroup
                      }
                      viewBy={
                        customVizRequest?.inputs.extras.timeBoiOption ?? 'year'
                      }
                      isMetricBoolean={isMetricBoolean}
                      isPrintMode={false}
                      config={lineChartConfig}
                    />
                  </Spacing>
                </div>
              )}
            </>
          )}
      </Spacing>
      {readyToDisplayDynamicContent &&
        !isEventStudyNotEnoughData &&
        customVizRequest && (
          <Spacing size="lg" direction="vertical" className="mt-8 px-4">
            <hr className="border-0 border-t border-[#E7E8E8]" />
            <TimeTrendsExplanation
              data={graphData}
              request={customVizRequest}
              myObservation={myObservation}
              downloadContainerRef={downloadContainerRef}
            />
          </Spacing>
        )}
    </div>
  );
};
